import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';
import { LoginService } from '../login-service/login.service';
import { NotificationService } from '../notification-service/notification.service';
import { BackendService } from '../backend-service/backend.service';
import {
	CommonErrorMessage,
	ErrorResponse,
	LoginInfo,
	UserUpdateRequest,
} from '@meta-press/shared-plugins';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';

@Component({
	selector: 'app-user-profile-modal',
	standalone: true,
	imports: [CommonModule, FormsModule, NgbAlertModule],
	templateUrl: './user-profile-modal.component.html',
	styleUrl: './user-profile-modal.component.scss',
})
export class UserProfileModalComponent implements OnInit {
	// User being edited (copy of current user)
	protected editingUser:
		| (LoginInfo & { oldPassword?: string; newPassword?: string })
		| null = null;

	// Store original user data for comparison
	private originalUserData: LoginInfo | null = null;

	// Loading and error states
	protected isLoading = false;
	protected errors: string[] = [];

	// Password visibility state
	protected showOldPassword = false;
	protected showNewPassword = false;

	constructor(
		public activeModal: NgbActiveModal,
		protected loginService: LoginService,
		private notificationService: NotificationService,
		private backendService: BackendService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		// Get current user and create editable copy
		const currentUser = this.loginService.GetCurrentUser();
		if (currentUser) {
			// Store original user data for comparison
			this.originalUserData = currentUser;
			this.editingUser = {
				...currentUser,
				oldPassword: '',
				newPassword: '',
			};
		} else {
			// This shouldn't happen, but just in case
			this.notificationService.ShowToast(
				'Käyttäjätietoja ei voitu ladata. Kirjaudu sisään uudelleen.',
				'danger',
			);
			this.activeModal.dismiss();
		}
	}

	/**
	 * Save user profile changes
	 */
	updateUser(): void {
		if (!this.editingUser || !this.originalUserData) return;

		// Clear previous errors
		this.errors = [];

		// Validate required fields
		if (!this.editingUser.firstName?.trim()) {
			this.errors.push('Etunimi on pakollinen');
		}
		if (!this.editingUser.lastName?.trim()) {
			this.errors.push('Sukunimi on pakollinen');
		}
		if (!this.editingUser.email?.trim()) {
			this.errors.push('Sähköposti on pakollinen');
		}

		// If changing password, validate old password is provided
		if (this.editingUser.newPassword && !this.editingUser.oldPassword) {
			this.errors.push(
				'Nykyinen salasana on pakollinen salasanan vaihtamiseksi',
			);
		}

		if (this.errors.length > 0) {
			this.cdr.detectChanges();
			return;
		}

		// Set loading state
		this.isLoading = true;

		const original = this.originalUserData;

		// Build update request with only changed fields
		// Set empty string for unchanged fields, -1 for unchanged role
		const updateRequest: UserUpdateRequest = {
			oldPassword: this.editingUser.oldPassword || '',
			newPassword: this.editingUser.newPassword || '',
			email:
				this.editingUser.email !== original.email
					? this.editingUser.email
					: '',
			firstName:
				this.editingUser.firstName !== original.firstName
					? this.editingUser.firstName
					: '',
			lastName:
				this.editingUser.lastName !== original.lastName
					? this.editingUser.lastName
					: '',
			role:
				this.editingUser.permission !== original.permission
					? this.editingUser.permission
					: -1,
		};

		this.backendService
			.UpdateCurrentUserInfo(updateRequest)
			.pipe(
				catchError((response: HttpErrorResponse) => {
					// Clear loading state
					this.isLoading = false;

					// Handle CommonErrorMessage format
					const error = response.error as CommonErrorMessage;
					console.error('Error updating user profile:', error);

					this.errors = [];
					if (error?.title && error?.message) {
						// Display title and message from CommonErrorMessage
						this.errors.push(`${error.title}: ${error.message}`);
					} else if (
						response.error?.errors &&
						Array.isArray(response.error.errors)
					) {
						// Handle ErrorResponse format (legacy)
						response.error.errors.forEach((e: string) => {
							this.errors.push(e);
						});
					} else if (response.message) {
						this.errors.push(response.message);
					} else {
						this.errors.push(
							'Tuntematon virhe käyttäjätietojen päivityksessä.',
						);
					}

					this.notificationService.ShowToast(
						'Käyttäjätietojen päivittäminen epäonnistui.',
						'danger',
					);

					// Manually trigger change detection to update the modal
					this.cdr.detectChanges();

					return throwError(
						() => new Error(error?.message || 'Update failed'),
					);
				}),
			)
			.subscribe({
				next: () => {
					// Update the current user data in login service
					const currentUser = this.loginService.GetCurrentUser();
					if (currentUser) {
						currentUser.firstName = this.editingUser!.firstName;
						currentUser.lastName = this.editingUser!.lastName;
						currentUser.email = this.editingUser!.email;
						// Note: password is not stored in LoginInfo for security
					}

					this.notificationService.ShowToast(
						'Käyttäjätietojen päivittäminen onnistui.',
						'success',
					);

					this.isLoading = false;
					this.activeModal.close(true);
				},
				error: () => {
					// Error already handled in catchError
					// Just ensure loading state is cleared
					this.isLoading = false;
				},
			});
	}

	/**
	 * Toggle password visibility for old password
	 */
	toggleOldPasswordVisibility(): void {
		this.showOldPassword = !this.showOldPassword;
	}

	/**
	 * Toggle password visibility for new password
	 */
	toggleNewPasswordVisibility(): void {
		this.showNewPassword = !this.showNewPassword;
	}

	/**
	 * Clear the new password field
	 */
	clearNewPassword(): void {
		if (this.editingUser) {
			this.editingUser.newPassword = '';
			this.editingUser.oldPassword = '';
		}
	}

	/**
	 * Check if password change fields are shown
	 */
	get isChangingPassword(): boolean {
		return !!(
			this.editingUser?.newPassword || this.editingUser?.oldPassword
		);
	}

	/**
	 * Cancel and close modal
	 */
	cancel(): void {
		this.activeModal.dismiss();
	}
}
